import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import IntervalInput, { parseIntervalInput, intervalToLatex } from "../../../../components/inputs/IntervalInput";

/* ============ helpers ============ */
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const EPS = 1e-9;

const gcd = (a, b) => { a = Math.abs(a); b = Math.abs(b); while (b) [a, b] = [b, a % b]; return a || 1; };
const simplifyFrac = (num, den) => { if (den < 0) { num = -num; den = -den; } const g = gcd(num, den); return { num: num / g, den: den / g }; };
const rawFracLatex = (num, den) => { if (den < 0) { num = -num; den = -den; } const s = num < 0 ? "-" : ""; const N = Math.abs(num), D = den; return `${s}\\dfrac{${N}}{${D}}`; };
const mixedLatex = (num, den) => {
  let { num: N, den: D } = simplifyFrac(num, den);
  const sign = N < 0 ? "-" : "";
  N = Math.abs(N);
  if (N % D === 0) return (sign ? `${sign}${N / D}` : `${N / D}`);
  const A = Math.floor(N / D), R = N % D;
  if (A > 0) return `${sign}${A}\\;\\dfrac{${R}}{${D}}`;
  return `${sign}\\dfrac{${R}}{${D}}`;
};

const withSign = (n) => (n >= 0 ? `+\\,${n}` : `-\\,${Math.abs(n)}`);
const coeffX  = (a) => (a === 1 ? "x" : a === -1 ? "-x" : `${a}x`);
const linLatex = (a, b) => `${coeffX(a)}${b === 0 ? "" : (b > 0 ? `+${b}` : `${b}`)}`;

function asInterval(left, leftClosed, right, rightClosed) { return { left, right, leftClosed, rightClosed }; }

function intervalsEqual(A, B) {
  if (A.length !== B.length) return false;
  for (let i = 0; i < A.length; i++) {
    const p = A[i], q = B[i];
    const le = (p.left === q.left) || (Math.abs(p.left - q.left) <= EPS);
    const re = (p.right === q.right) || (Math.abs(p.right - q.right) <= EPS);
    if (!(le && re)) return false;
    const lcOK = (p.left === -Infinity || q.left === -Infinity) ? true : p.leftClosed === q.leftClosed;
    const rcOK = (p.right === Infinity  || q.right === Infinity) ? true : p.rightClosed === q.rightClosed;
    if (!(lcOK && rcOK)) return false;
  }
  return true;
}
function parsedToIntervals(parsed) {
  if (!parsed) return [];
  if (parsed.kind === "all") return [asInterval(-Infinity, false, Infinity, false)];
  if (parsed.kind === "empty") return [];
  if (parsed.kind === "intervals") return parsed.intervals || [];
  if (parsed.kind === "complement") {
    const pts = (parsed.points || (parsed.point != null ? [parsed.point] : [])).slice().sort((a,b)=>a-b);
    if (!pts.length) return [asInterval(-Infinity, false, Infinity, false)];
    const out = [];
    let curLeft = -Infinity;
    for (const x of pts) {
      out.push(asInterval(curLeft, false, x, false));
      curLeft = x;
    }
    out.push(asInterval(curLeft, false, Infinity, false));
    return out;
  }
  return [];
}
function intervalsToRaw(list) {
  const part = (L, LC, R, RC) => {
    const lb = LC ? "[" : "(";
    const rb = RC ? "]" : ")";
    const Ls = (L === -Infinity) ? "-∞" : String(L);
    const Rs = (R === Infinity)  ? "∞"  : String(R);
    return `${lb}${Ls}, ${Rs}${rb}`;
  };
  return list.map(I => part(I.left, I.leftClosed, I.right, I.rightClosed)).join(" ∪ ");
}

/* ============ generator ============ */
export function generateTask() {
  const variant = Math.random() < 0.5 ? "base" : "arg";
  // zakresy uzgodnione: a ∈ {-5..-1,1..5}, b ∈ {1..15}
  const A_POOL = [-5,-4,-3,-2,-1,1,2,3,4,5];
  const B_POOL = [1,2,3,4,5,6,7,8,9,10,11,12,13,14,15];
  const a = pick(A_POOL);
  const b = pick(B_POOL);

  if (variant === "base") {
    return { type: "LogDomainTask", data: { variant, a, b, base: true, argConst: 5 } };
  } else {
    const k = pick([2,3,4,5,10]); // stała podstawa (>0, ≠1)
    return { type: "LogDomainTask", data: { variant, a, b, baseConst: k } };
  }
}

/* ============ render ============ */
export function renderTask({ data }) {
  const { a, b } = data;
  const lin = linLatex(a, b);

  if (data.variant === "base") {
    const base = lin;
    return (
      <div style={{ marginBottom: "12px" }}>
        <div>Określ dziedzinę wyrażenia:</div>
        <BlockMath math={`\\log_{${base}} 5`} />
      </div>
    );
  } else {
    const arg = lin;
    return (
      <div style={{ marginBottom: "12px" }}>
        <div>Określ dziedzinę wyrażenia:</div>
        <BlockMath math={`\\log_{${data.baseConst}}(${arg})`} />
      </div>
    );
  }
}


export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <IntervalInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      previewWithX={true}
      prefixAbove={true}   // nad polem: x \in
    />
  );
}

/* ============ logika dziedziny ============ */
function expectedIntervals({ variant, a, b }) {
  const p = -b / a;                 // granica z ax+b>0
  if (variant === "arg") {
    return (a > 0)
      ? [asInterval(p, false, Infinity, false)]
      : [asInterval(-Infinity, false, p, false)];
  }
  // variant === "base": ax+b > 0 i ax+b ≠ 1
  const q = (1 - b) / a;            // punkt do wykluczenia
  if (a > 0) {
    if (q > p + EPS) return [asInterval(p, false, q, false), asInterval(q, false, Infinity, false)];
    return [asInterval(p, false, Infinity, false)];
  } else {
    if (q < p - EPS) return [asInterval(-Infinity, false, q, false), asInterval(q, false, p, false)];
    return [asInterval(-Infinity, false, p, false)];
  }
}

/* ============ ładne LaTeX dla dziedziny (z ułamkami mieszanymi) ============ */
function domainLatex(data) {
  const exp = expectedIntervals(data);
  const { a, b } = data;

  // punkty specjalne: p = -b/a, q = (1-b)/a
  const pNum = -b,    pDen = a, pVal = pNum / pDen;
  const qNum = 1 - b, qDen = a, qVal = qNum / qDen;

  const pointLatex = (x) => {
    if (x === -Infinity) return "-\\infty";
    if (x === Infinity)  return "\\infty";
    if (Math.abs(x - pVal) <= EPS) return mixedLatex(pNum, pDen);
    if (Math.abs(x - qVal) <= EPS) return mixedLatex(qNum, qDen);
    // awaryjnie – powinno prawie nigdy nie wystąpić:
    return String(x);
  };

  const part = (I) => {
    const lb = I.leftClosed ? "[" : "(";
    const rb = I.rightClosed ? "]" : ")";
    const L = pointLatex(I.left);
    const R = pointLatex(I.right);
    return `${lb}${L},${R}${rb}`;
  };

  const body = exp.map(part).join("\\cup");
  return `x\\in ${body}`;
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object") return false;
  const parsed = userAnswer.parsed;
  if (!parsed) return false;
  const exp = expectedIntervals(data);
  const got = parsedToIntervals(parsed);
  return intervalsEqual(exp, got);
}

/* ============ odpowiedź ============ */
export function renderAnswer({ data }) {
  const latex = domainLatex(data);
  return <InlineMath math={latex} />;
}

/* ============ wyjaśnienia: „szynowe” przekształcenia ============ */
export function renderExplanation({ data }) {
  const { a, b } = data;
  const lin = linLatex(a, b);

  // dla nierówności ax+b > 0:
  const barMove = b > 0 ? `-\\,${b}` : `+\\,${Math.abs(b)}`;
  const rhsAfterMove = -b;                    // 0 - b
  const barDiv = `:\\,(${a})`;                // bez komentarza o odwróceniu znaku
  const t_num = -b, t_den = a;
  const t_val = t_num / t_den;
  const rawT = rawFracLatex(t_num, t_den);
  const simpT = mixedLatex(t_num, t_den);
  const needSimp = rawT !== simpT;
  const finalSign = (a > 0) ? ">" : "<";

  const L1 = String.raw`${lin} &\; > &\; 0 & \quad \big|\, ${barMove}`;
  const L2 = String.raw`${coeffX(a)} &\; > &\; ${rhsAfterMove} & \quad \big|\, ${barDiv}`;
  const L3 = String.raw`x &\; ${finalSign} &\; ${rawT} & `;
  const L4 = String.raw`x &\; ${finalSign} &\; ${simpT} & `;

  const mathAligned = needSimp
    ? String.raw`\begin{alignedat}{2}
        ${L1} \\
        ${L2} \\
        ${L3} \\[8pt]
        ${L4}
      \end{alignedat}`
    : String.raw`\begin{alignedat}{2}
        ${L1} \\
        ${L2} \\
        ${L3}
      \end{alignedat}`;

  const domLatex = domainLatex(data);

  if (data.variant === "base") {
    // dodatkowo: ax+b ≠ 1  →  x ≠ (1-b)/a
    const q_num = 1 - b, q_den = a;
    const qLatex = mixedLatex(q_num, q_den);

    return (
      <div className="space-y-2">
        <p><strong>Krok 1:</strong> Warunki: <InlineMath math={"a>0,\\; a\\neq 1,\\; b>0"} /></p>

        <p><strong>Krok 2:</strong> Podstawienie:</p>
        <BlockMath math={`${lin}>0,\\quad ${lin}\\neq 1`} />

        <p><strong>Krok 3:</strong> Rozwiązanie nierówności:</p>
        <BlockMath math={mathAligned} />

        <p><strong>Krok 4:</strong> Wykluczenie punktu:</p>
        <BlockMath math={`x\\neq ${qLatex}`} />

        <p><strong>Dziedzina:</strong></p>
        <BlockMath math={domLatex} />
      </div>
    );
  }

  // variant === "arg"
  return (
    <div className="space-y-2">
      <p><strong>Krok 1:</strong> Warunki: <InlineMath math={"a>0,\\; a\\neq 1,\\; b>0"} /></p>

      <p><strong>Krok 2:</strong> Podstawienie:</p>
      <BlockMath math={`${lin}>0`} />

      <p><strong>Krok 3:</strong> Rozwiązanie nierówności:</p>
      <BlockMath math={mathAligned} />

      <p><strong>Dziedzina:</strong></p>
      <BlockMath math={domLatex} />
    </div>
  );
}

/* ============ podgląd odpowiedzi użytkownika ============ */
export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) {
    return <i>(brak odpowiedzi)</i>;
  }
  return <InlineMath math={intervalToLatex(userAnswer.raw, { withX: true })} />;
}
